import { formatCurrency } from 'lib/format/formatCurrencyIntl'
import { dateDifference } from 'lib/datetime/dateUtils'
import type { CruisesContract } from '@luxuryescapes/contract-svc-cruise'

type CruiseInclusionsMapParams = {
  inclusions: Array<App.Cruises.OfferInclusion>
  departureId: string
  lowestRateCode: string
  regionCode: string
  /* Displays inclusions for all rate codes, not restricted to the lowest rate */
  allRateCodes?: Array<string>
  /* If true, we want to consider all offer inclusions for the main departure (displayed in the offer tile) */
  isMainDeparture?: boolean
}

type CruiseInclusionsMapResult = {
  luxPlusInclusionsByTier: App.LuxPlusInclusionsByTier | undefined
  standardInclusions: Array<App.PackageInclusion>
  inclusionDetails: App.Cruises.OfferInclusionDetails
}

export function cruiseInclusionsMap({
  inclusions,
  departureId,
  lowestRateCode,
  allRateCodes,
  regionCode,
  isMainDeparture = false,
}: CruiseInclusionsMapParams): CruiseInclusionsMapResult {
  const rateCodeFilters = allRateCodes?.length ? allRateCodes : [lowestRateCode]
  const filteredInclusions = isMainDeparture ? inclusions : inclusions
    .filter(({ departureIds, rateCodes }) => (
      (!departureIds.length || departureIds.includes(departureId)) &&
      (!rateCodes.length || rateCodeFilters.some(rateFilter => rateCodes.includes(rateFilter)))
    ))

  return {
    luxPlusInclusionsByTier: luxPlusInclusionsByTierMap(filteredInclusions, regionCode),
    standardInclusions: standardInclusionsMap(filteredInclusions, regionCode),
    inclusionDetails: cruiseInclusionDetailsMap(filteredInclusions),
  }
}

export function cruiseBookingInclusionsMap(
  inclusions: Array<CruisesContract.BookingInclusion>,
  regionCode: string,
): {
  standardInclusions: Array<App.PackageInclusion>
  luxPlusInclusions: Array<App.PackageInclusion>
} {
  const mappedInclusions = inclusions.map((inclusion) => {
    const description = getInclusionDescription(regionCode, inclusion.description, inclusion.amount, inclusion.currency)

    return {
      id: inclusion.id,
      description,
      type: inclusion.inclusionType,
      parentCategory: inclusion.inclusionItemType as App.PackageInclusionParentCategory,
      isHighlighted: inclusion.inclusionType === 'LUX_PLUS',
      symbol: getInclusionSymbol(inclusion.inclusionItemType),
    }
  })

  const standardInclusions = mappedInclusions.filter((inclusion) => inclusion.type !== 'LUX_PLUS')
  const luxPlusInclusions = mappedInclusions.filter((inclusion) => inclusion.type === 'LUX_PLUS')

  return {
    standardInclusions,
    luxPlusInclusions,
  }
}

function luxPlusInclusionsByTierMap(
  inclusions: Array<App.Cruises.OfferInclusion>,
  regionCode: string,
): App.LuxPlusInclusionsByTier | undefined {
  const luxPlusInclusionGroup = getFirstLuxPlusInclusion(inclusions)

  if (!luxPlusInclusionGroup?.items?.length) return

  const packageInclusions: Array<App.PackageInclusion> = luxPlusInclusionGroup.items.map((item) => {
    const description = getInclusionDescription(regionCode, item.description, item.creditAmount, item.creditCurrency)

    return {
      id: item.id,
      description,
      type: luxPlusInclusionGroup.type,
      parentCategory: item.type,
      isHighlighted: luxPlusInclusionGroup.type === 'LUX_PLUS',
      symbol: getInclusionSymbol(item.type),
      inclusionGroupId: luxPlusInclusionGroup.id,
    }
  })

  if (!packageInclusions.length) return

  return {
    // Cruise only has one tier of luxplus inclusions for now
    base: packageInclusions,
  }
}

function standardInclusionsMap(
  inclusions: Array<App.Cruises.OfferInclusion>,
  regionCode: string,
): Array<App.PackageInclusion> {
  const standardInclusionGroup = getFirstStandardInclusion(inclusions)
  if (!standardInclusionGroup) return []

  const packageInclusions: Array<App.PackageInclusion> = standardInclusionGroup.items.map((item) => {
    const description = getInclusionDescription(regionCode, item.description, item.creditAmount, item.creditCurrency)

    return {
      id: item.id,
      description,
      isHighlighted: item.isHighlighted,
      type: standardInclusionGroup.type,
      parentCategory: item.type,
      symbol: getInclusionSymbol(item.type),
      inclusionGroupId: standardInclusionGroup.id,
    }
  })

  return packageInclusions
}

function cruiseInclusionDetailsMap(inclusions: Array<App.Cruises.OfferInclusion>): App.Cruises.OfferInclusionDetails {
  const chosenStandardInclusion = getFirstStandardInclusion(inclusions)
  const luxPlusInclusion = getFirstLuxPlusInclusion(inclusions)

  const hasNewInclusion =
    chosenStandardInclusion && dateDifference(new Date(), new Date(chosenStandardInclusion.startDate)).days < 7 ||
    luxPlusInclusion && dateDifference(new Date(), new Date(luxPlusInclusion.startDate)).days < 7

  const standardInclusionEndDate = chosenStandardInclusion?.endDate ? new Date(chosenStandardInclusion?.endDate) : undefined
  const luxPlusInclusionEndDate = luxPlusInclusion?.endDate ? new Date(luxPlusInclusion?.endDate) : undefined
  let earliestEndDate = chosenStandardInclusion?.endDate ?? luxPlusInclusion?.endDate

  if (standardInclusionEndDate && luxPlusInclusionEndDate && standardInclusionEndDate > luxPlusInclusionEndDate) {
    earliestEndDate = luxPlusInclusion?.endDate
  }

  return {
    hasNewInclusion: hasNewInclusion ?? false,
    inclusionEndDate: earliestEndDate ?? '',
  }
}

/**
 * There should not be more than one group of Standard inclusions, but if there is, we will use only the first one.
 */
function getFirstStandardInclusion(inclusions: Array<App.Cruises.OfferInclusion>): App.Cruises.OfferInclusion | undefined {
  return inclusions.find(({ type }) => type !== 'LUX_PLUS')
}

/**
 * There should not be more than one group of LuxPlus inclusions, but if there is, we will use only the first one.
 */
function getFirstLuxPlusInclusion(inclusions: Array<App.Cruises.OfferInclusion>): App.Cruises.OfferInclusion | undefined {
  return inclusions.find(({ type }) => type === 'LUX_PLUS')
}

function getInclusionDescription(
  regionCode: string,
  description?: string | null,
  amount?: number | null,
  currency?: string | null,
): string {
  if (amount && currency) {
    return `Up to ${formatCurrency(amount, currency, regionCode)} Onboard Credit`
  }

  return description ?? ''
}

function getInclusionSymbol(type: string): App.OfferFeatureSymbol | undefined {
  if (type === 'ONBOARD_CREDIT') {
    return 'dollar_sign_in_circle'
  }

  return undefined
}
